import textConvertor from './textConvertor';
import positiveNegativeBarChartSmallConvertor from './positiveNegativeBarChartSmallConvertor';
//import chart from "../PositiveNegativeBarChartSmall/chart"
//import PigeonDocNodeModel from '../../models/PigeonDocNodeModel';
import _cloneDeep from 'lodash/cloneDeep';

const MAX_COLS = 7;
const DEFAULT_COL_SIZE = 90;

/**
 * create pdfmake table object
 * @param {Array} body 2d array of table data
 * @param {Array} widths array of column widths
 * @returns {Object} pdfmake table object with default layout
 **/
const constructTableObject = (body, widths = []) => {
  const tableObj = {
    table: {
      headerRows: 0,
      body: body,
    },
    layout: {
      fillColor: function (rowIndex) {
        return rowIndex === 0 ? '#f2f2f2' : null;
      },
      hLineWidth: function (i, node) {
        let w = 1;
        switch (i) {
          case 0:
          case node.table.body.length:
            w = 0;
            break;
          case 1:
            w = 2;
            break;
          default:
            break;
        }
        return w;
      },
      vLineWidth: function () {
        return 0;
      },
      hLineColor: function () {
        return '#cccccc';
      },
    },
    style: 'defaultTable',
  };
  if (widths.length > 0) tableObj.table.widths = widths;
  return tableObj;
};

/**
 * format all cell data with html to pdfmake text content
 * @param {Array} row row data
 * Note that colum 4 (index 3) is a hard code for bypassing into a posneg chart
 **/
const formatTextInCells = (object) => {
  object.row.forEach((cell, indx) => {
    if (object.isTextThemeTable) {
      if (indx === 3) {
        return;
      }
    }
    // table cell has set style
    if (cell.style) {
      object.row[indx] = textConvertor(cell.value);
      return;
    }
    object.row[indx] = textConvertor(cell);
  });
};

/**
 * format colum headers
 * @param {Array} columnNames array of column headers
 * @returns {Array} formatted table headers
 **/
const formatTableHeaders = (columnNames) => {
  let headers = [];
  columnNames.forEach((col) => {
    headers.push({
      text: col,
      bold: true,
    });
  });
  return headers;
};

/**
 * get row data for columns from startIndx to sendIndx
 * @param {Array} rows 2d array of row data
 * @param {Integer} startIndx to start slice (start column index)
 * @param {Integer} endIndx to end slice (end column index)
 * @returns {Array} row data
 **/
const getRowsForCols = (rows, startIndx, endIndx, fixedColumnsIndx) => {
  return rows.reduce((allRows, row) => {
    let rowData = [];
    let fixed = [];
    let sliced = row.slice(startIndx, endIndx);
    if (startIndx > 0 && fixedColumnsIndx.length > 0) {
      fixedColumnsIndx.forEach((indx) => {
        fixed.push(_cloneDeep(row[indx]));
      });
    } else if (startIndx > 0) {
      fixed.push(_cloneDeep(row[0]));
    }
    rowData = [...fixed, ...sliced];
    allRows.push(rowData);
    return allRows;
  }, []);
};

/**
 * create separate tables if the table has too many columns
 * to prevent it from getting cutt off
 * @param {Array} columnNames array of column headers
 * @param {Array} rows 2d array of row data
 * @returns {Array} of pdfmake tables
 *
 * TODO: while loop can be cleaned up and moved to its own function
 **/
const getSeparatedTables = (columnNames, fixedColumnsIndx = [], rows) => {
  let tables = [];
  let startIndx = 0;
  let endIndx = MAX_COLS;
  let body = [];
  let defaultwidths = new Array(MAX_COLS).fill(DEFAULT_COL_SIZE);
  let fixedColNames = [];

  // set array of fixed col names
  if (fixedColumnsIndx.length > 0) {
    fixedColumnsIndx.forEach((indx) => {
      fixedColNames.push(columnNames[indx]);
    });
  }

  // separate table data into MAX_COLS by getting splices of data & constructing a table
  do {
    // creating second table to update the endIndx
    if (startIndx > 0 && fixedColumnsIndx.length > 0)
      endIndx = endIndx + 1 - fixedColumnsIndx.length;

    /***
     * COLUMNS
     *
     * startIndx > 0 = creating the second table, add fixed headers to table
     * else just creating the first table so slice the columNames
     *
     **/
    let colNames = [];
    if (startIndx > 0) {
      let currColNames = colNames.concat(columnNames.slice(startIndx, endIndx));
      if (fixedColumnsIndx.length > 0) {
        colNames = [...fixedColNames, ...currColNames];
      } else {
        colNames = [columnNames[0], ...currColNames]; // no fixed cols set, just add row header
      }
    } else {
      colNames = columnNames.slice(startIndx, endIndx);
    }
    body.push(formatTableHeaders(colNames));

    /***
     * ROWS
     **/
    body = body.concat(getRowsForCols(rows, startIndx, endIndx, fixedColumnsIndx));

    let table = constructTableObject(body, defaultwidths);
    if (startIndx > 0) table.pageBreak = 'before';
    tables.push(table);

    startIndx = endIndx;
    endIndx = startIndx + (MAX_COLS - 1);
    body = [];
  } while (columnNames.length - startIndx + fixedColumnsIndx.length > MAX_COLS);

  let lastColNames = [];
  if (fixedColumnsIndx.length > 0) {
    lastColNames = [...fixedColNames, ...columnNames.slice(startIndx)];
  } else {
    lastColNames = [columnNames[0], ...columnNames.slice(startIndx)]; // no fixed cols set, just add row header
  }
  body.push(formatTableHeaders(lastColNames));
  body = body.concat(getRowsForCols(rows, startIndx, endIndx, fixedColumnsIndx));

  const lastTableWidths = defaultwidths.slice(0, body[0].length);
  let lastTable = constructTableObject(body, lastTableWidths);
  lastTable.pageBreak = 'before';
  tables.push(lastTable);

  return tables;
};

/*********************************************************
 * format pigeondoc table to a pdfmake object
 * @param {Object} node pigeondoc table node
 * @returns {Object | Array} has 2 possible return values
 * 1. a single pdfmake table [see constructTableObject]
 * 2. if table has too many columns it will break it into multiple tables
 * so you get an array of pdfmake tables
 * TODO: cleanup/simplify, add ability to set colspan and rowspan in pigeondoc table nodes?
 *********************************************************/
export default async (node) => {
  const fixedColumnsIndx =
    node.meta && node.meta.fixedColumnsIndx ? node.meta.fixedColumnsIndx : [];
  const columnNames = node.content.data.columnNames;
  const rowNames = node.content.data.rowNames;
  const rows = _cloneDeep(node.content.data.data);
  const max = Math.max(...rows.map((row) => row.length)); // max colums for this table node
  let storeRowSpan = []; // temp store the rows that need rowSpan

  let isTextThemeTable = false;
  if (node.type === 'textThemeTable') {
    isTextThemeTable = true;
  }
  // Note that the 4th column is hardcoded to be a chart.
  if (isTextThemeTable) {
    let tempRows = _cloneDeep(rows);
    for (let i = 0; i < tempRows.length; i++) {
      if (tempRows[i][3]) {
        rows[i].splice(3, 1, await positiveNegativeBarChartSmallConvertor(tempRows[i][3]));
      }
    }
  }

  // go through each row of data
  rows.forEach((row, indx) => {
    // handle row spans
    if (Array.isArray(row[0])) {
      // construct rows within row span
      let tempRows = _cloneDeep(row);
      tempRows.forEach((row2, i) => {
        formatTextInCells({ row: row2 });
        if (i === 0) row2.unshift({ rowSpan: row.length, text: `${rowNames[indx]}` });
        else row2.unshift('');
      });
      storeRowSpan.push({
        startIndx: indx,
        rows: tempRows,
      });
      return;
    }

    formatTextInCells({ row: row, isTextThemeTable: isTextThemeTable });

    if (row.length < max) {
      row[row.length - 1] = { colSpan: row.length + (max - row.length), text: row[row.length - 1] };
    }

    // append row names to start of row items
    row.unshift(rowNames[indx]);
  });

  // add rows with row span details
  if (storeRowSpan.length > 0) {
    storeRowSpan.forEach((storedRows) => {
      rows.splice(storedRows.startIndx, 1);
      rows.splice(storedRows.startIndx, 0, ...storedRows.rows);
    });
  }

  // break into separate tables if more than MAX_COLS columns
  if (columnNames.length > MAX_COLS) {
    const separatedTables = getSeparatedTables(columnNames, fixedColumnsIndx, rows);
    return separatedTables;
  } else {
    let body = [];
    body.push(formatTableHeaders(columnNames)); //pdfmake requires first item to be the column headers
    body = body.concat(rows);
    return [constructTableObject(body)];
  }
};
